import { memo } from 'preact/compat'
import { Radio } from 'lucide-preact'
import type { AnalyticsChannel, ChannelBreakdownDto } from '@/types/analytics'
import { MetricBarList, type BarListItem } from './MetricBarList'

interface Props {
  channels: ChannelBreakdownDto | undefined
  className?: string
  loading?: boolean
  error?: string | null
}

/** Display order + bar colour for each marketing channel. */
const CHANNEL_CONFIG: { key: AnalyticsChannel; label: string; barClass: string }[] = [
  { key: 'Email', label: 'Email', barClass: 'bg-gradient-to-r from-sky-400 to-sky-500' },
  { key: 'Paid', label: 'Paid', barClass: 'bg-gradient-to-r from-violet-400 to-violet-500' },
  { key: 'Social', label: 'Social', barClass: 'bg-gradient-to-r from-pink-400 to-pink-500' },
  { key: 'Organic', label: 'Organic', barClass: 'bg-gradient-to-r from-emerald-400 to-emerald-500' },
  { key: 'Direct', label: 'Direct', barClass: 'bg-gradient-to-r from-slate-400 to-slate-500' },
]

/**
 * Sessions split across the five marketing channels (ORB-373), rendered with the shared
 * bar list in a fixed channel order so the breakdown reads consistently between scopes.
 */
export const ChannelBreakdownTile = memo(({ channels, className, loading, error }: Props) => {
  // A channel bucket can be absent when the API/mock omits a channel with no sessions, so
  // read it defensively (missing → 0) rather than assuming all five keys are present.
  const items: BarListItem[] = channels
    ? CHANNEL_CONFIG.map(c => ({ label: c.label, value: channels[c.key]?.Current ?? 0, barClass: c.barClass }))
    : []

  return (
    <MetricBarList
      title="Channels"
      icon={<Radio className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
      items={items}
      emptyMessage="No channel data yet."
      maxItems={CHANNEL_CONFIG.length}
      className={className}
      loading={loading}
      error={error}
    />
  )
})
